import { useCallback, useEffect, useState } from "react";
import type { WorkshopCategoryNumber } from "@verity-connect/domain";

/** A local copy of the seminars a teacher ticked on the voucher. Signed-in
 * teachers can also save the same choices to their account for school review.
 * This copy keeps the form usable before sign-in and during a lost connection.
 */
const STORAGE_PREFIX = "wvcea-continuing-education-v2";

export interface VoucherSelectionScope {
  tenantId: string;
  eventId: string;
  personId: string;
}

export interface VoucherSelection {
  /** Sessions she has confirmed she actually sat in. */
  attendedItemIds: readonly string[];
  /** Only where she has overridden what the app proposed. */
  categoryByItemId: Readonly<Record<string, WorkshopCategoryNumber>>;
}

const EMPTY: VoucherSelection = { attendedItemIds: [], categoryByItemId: {} };

export function useVoucherSelection(scope: VoucherSelectionScope) {
  const storageKey = voucherSelectionStorageKey(scope);
  const [selection, setSelection] = useState<VoucherSelection>(() => read(storageKey));

  useEffect(() => setSelection(read(storageKey)), [storageKey]);

  const save = useCallback((next: VoucherSelection) => {
    setSelection(next);
    write(storageKey, next);
  }, [storageKey]);

  const toggleAttended = useCallback((itemId: string) => {
    setSelection((current) => {
      const attended = new Set(current.attendedItemIds);
      if (attended.has(itemId)) attended.delete(itemId);
      else attended.add(itemId);
      const next = { ...current, attendedItemIds: [...attended] };
      write(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const setCategory = useCallback((itemId: string, category: WorkshopCategoryNumber) => {
    setSelection((current) => {
      const next = { ...current, categoryByItemId: { ...current.categoryByItemId, [itemId]: category } };
      write(storageKey, next);
      return next;
    });
  }, [storageKey]);

  return { selection, toggleAttended, setCategory, save };
}

/** No teacher, event or association can reuse another scope's offline draft. */
export function voucherSelectionStorageKey(scope: VoucherSelectionScope): string {
  return [STORAGE_PREFIX, scope.tenantId, scope.eventId, scope.personId]
    .map((part) => encodeURIComponent(part))
    .join(":");
}

function read(storageKey: string): VoucherSelection {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<VoucherSelection>;
    return {
      attendedItemIds: Array.isArray(parsed.attendedItemIds) ? parsed.attendedItemIds.filter(isString) : [],
      categoryByItemId: isRecord(parsed.categoryByItemId) ? parsed.categoryByItemId : {},
    };
  } catch {
    return EMPTY;
  }
}

function write(storageKey: string, selection: VoucherSelection): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(selection));
  } catch {
    // The form is still printable from what is on screen when device storage is
    // full or blocked.
  }
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isRecord(value: unknown): value is Readonly<Record<string, WorkshopCategoryNumber>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
